import {
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGetBranchesForSystemQuery } from '@app/api/Branches';
import { useGetLegalEntitiesForSystemQuery } from '@app/api/LegalEntities';
import type { GetSocialIntegrationsResponse } from '@app/api/SocialIntegrations';
import { SetBranchesComponent } from '@app/components';
import {
  BRANCHES_LABEL,
  CANCEL,
  PREFIX,
  RULES_IS_REQUIRE,
  SAVE,
  SUCCESS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import {
  TAX_SYSTEM_CODES,
  VAT_CODES,
} from '@app/pages/Integrations/IntegrationsList/data/constants';
import { IntegrationsContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useCreateOrUpdateYookassaIntegrationMutation,
  useLazyGetYookassaIntegrationQuery,
} from '@app/services/rtkQuery/SocialIntegrations/api';
import type { NBranches } from '@app/types';

const { Text } = Typography;

interface YooKassaModalProps {
  getData: () => Promise<GetSocialIntegrationsResponse | undefined>;
}

export const YooKassaModal: React.FC<YooKassaModalProps> = ({ getData }) => {
  const [isBranchesComponentOpen, setIsBranchesComponentOpen] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<
    NBranches.ModelBranchItem[]
  >([]);

  const {
    isYooKassaModalOpen,
    setIsYooKassaModalOpen,
    currentYooKassaIntegrationId,
    setCurrentYooKassaIntegrationId,
  } = useContext(IntegrationsContext);
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const [form] = useForm();
  const { showNotification } = useNotification();

  const [searchParams, setSearchParams] = useSearchParams();
  const queryYooKassaCode = searchParams.get('code');

  const [updateYookassaIntegration, { isLoading }] =
    useCreateOrUpdateYookassaIntegrationMutation();
  const [
    GetYookassaIntegration,
    { isFetching: isGetYookassaIntegrationLoading },
  ] = useLazyGetYookassaIntegrationQuery();
  const { data: branches, error: branchesError } = useGetBranchesForSystemQuery(
    { ActiveBranchId },
    { skip: !isYooKassaModalOpen }
  );
  const { data: legalEntities, error: legalEntitiesError } =
    useGetLegalEntitiesForSystemQuery({
      ActiveBranchId,
      includeDeleted: false,
    });

  useEffect(() => {
    if (branchesError) {
      showNotification(handleCatchError(branchesError));
    }
  }, [branchesError, showNotification]);

  useEffect(() => {
    const fetchYooKassaIntegration = async () => {
      try {
        if (!currentYooKassaIntegrationId) return;

        const data = await GetYookassaIntegration({
          ActiveBranchId,
          integrationId: currentYooKassaIntegrationId,
        }).unwrap();

        form.setFieldsValue({
          IntegrationName: data.IntegrationName,
          LegalEntityId: data.LegalEntityId,
          PaymentMethod: data.PaymentMethod,
          TaxSystemCode: data.TaxSystemCode,
          VatCode: data.VatCode,
          UseEmail: data.UseEmail,
        });
        const selectedBranches = (branches || []).filter((branch) =>
          data.BranchesIds.includes(branch.Id)
        );
        setSelectedBranches(selectedBranches);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

    if (isYooKassaModalOpen && !queryYooKassaCode) {
      fetchYooKassaIntegration();
    }
  }, [
    ActiveBranchId,
    GetYookassaIntegration,
    branches,
    form,
    isYooKassaModalOpen,
    showNotification,
    queryYooKassaCode,
    currentYooKassaIntegrationId,
  ]);

  useEffect(() => {
    if (legalEntitiesError) {
      showNotification(handleCatchError(legalEntitiesError));
    }
  }, [legalEntitiesError, showNotification]);

  const handleCancelModal = () => {
    if (searchParams.has('code')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('code');
      setSearchParams(nextParams, { replace: true });
    }

    setIsYooKassaModalOpen(false);
    setCurrentYooKassaIntegrationId(null);
    form.resetFields();
    setSelectedBranches([]);
  };

  const handleSubmitForm = async () => {
    const {
      IntegrationName,
      LegalEntityId,
      PaymentMethod,
      TaxSystemCode,
      VatCode,
      UseEmail,
    } = form.getFieldsValue();

    try {
      const requestData = {
        Id: currentYooKassaIntegrationId,
        ActiveBranchId,
        Code: queryYooKassaCode,
        LegalEntityId,
        BranchesIds: selectedBranches.map(({ Id }) => Id),
        PaymentMethod,
        IntegrationName,
        TaxSystemCode,
        VatCode,
        UseEmail,
      };
      await updateYookassaIntegration(requestData).unwrap();

      showNotification('Данные успешно сохранены', SUCCESS);
      getData();
      handleCancelModal();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  if (isGetYookassaIntegrationLoading) {
    return <Skeleton active />;
  }

  return (
    <Modal
      title={
        currentYooKassaIntegrationId
          ? 'Редактирование интеграции ЮKassa'
          : 'Добавление интеграции ЮKassa'
      }
      className={`${PREFIX}add-yoo-kassa-modal`}
      open={isYooKassaModalOpen}
      onCancel={handleCancelModal}
      onOk={form.submit}
      cancelText={CANCEL}
      okText={SAVE}
      okButtonProps={{
        loading: isLoading,
      }}
      cancelButtonProps={{
        disabled: isLoading,
      }}
    >
      <Form form={form} onFinish={handleSubmitForm}>
        <div className="container">
          <div className="item-container">
            <Text strong className="label label-require">
              Название
            </Text>
            <Form.Item name="IntegrationName" rules={[...RULES_IS_REQUIRE]}>
              <Input maxLength={140} />
            </Form.Item>
          </div>
          <div className="item-container">
            <Text strong className="label label-require">
              Юридическое лицо
            </Text>
            <Form.Item name="LegalEntityId" rules={[...RULES_IS_REQUIRE]}>
              <Select
                disabled={Boolean(currentYooKassaIntegrationId)}
                options={(legalEntities || []).map(({ Name, Id }) => {
                  return {
                    label: Name,
                    value: Id,
                  };
                })}
              />
            </Form.Item>
          </div>
          <div className="item-container">
            <Text strong className="label label-require">
              Система налогообложения
            </Text>
            <Form.Item name="TaxSystemCode" rules={[...RULES_IS_REQUIRE]}>
              <Select options={TAX_SYSTEM_CODES} />
            </Form.Item>
          </div>
          <div className="item-container">
            <Text strong className="label label-require">
              Ставка НДС
            </Text>
            <Form.Item name="VatCode" rules={[...RULES_IS_REQUIRE]}>
              <Select options={VAT_CODES} />
            </Form.Item>
          </div>
          <Space>
            <Text strong className="label">
              Формирование чеков
            </Text>
            <Form.Item
              name="UseEmail"
              valuePropName="checked"
              className="email-checkbox"
            >
              <Checkbox />
            </Form.Item>
          </Space>
          <div className="branches-container">
            <Typography.Title level={3} className="mg-0" data-testid="branches">
              {BRANCHES_LABEL}
            </Typography.Title>
            <Text type="secondary" data-testid="branches-hint">
              Укажите, в каких филиалах создать интеграцию
            </Text>
            <SetBranchesComponent
              open={isBranchesComponentOpen}
              onCancel={() => setIsBranchesComponentOpen(false)}
              selectedBranches={selectedBranches}
              setSelectedBranches={setSelectedBranches}
              allBranches={branches || []}
              showUserModal={() => setIsBranchesComponentOpen(true)}
            />
          </div>
        </div>
      </Form>
    </Modal>
  );
};
